import React, { useReducer, useEffect, useState, useRef } from 'react';
import { useRomaine, Romaine, Canvas } from 'romaine';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const FolderSelection = (_a) => {
    var { handleDrop, getFiles, children, image } = _a, props = __rest(_a, ["handleDrop", "getFiles", "children", "image"]);
    const initialInputStyles = {
        display: "none",
    };
    const initialLabelStyles = {
        position: "relative",
        display: "grid",
        placeItems: "center",
        width: "100%",
        height: "100%",
        outline: "thin solid black",
    };
    const initialDivStyles = {
        position: "absolute",
        inset: 0,
        zIndex: 2000,
        backgroundColor: "white",
    };
    const hoverInputStyles = {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        display: "block",
        visibility: "hidden",
    };
    const hoverLabelStyles = {
        position: "relative",
        display: "grid",
        placeItems: "center",
        width: "100%",
        height: "100%",
        outline: "thin solid black",
        backgroundColor: "lightblue",
    };
    const hoverDivStyles = {
        position: "absolute",
        inset: 0,
        backgroundColor: "white",
        zIndex: 2000,
    };
    const finalInputStyles = {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        display: "block",
        visibility: "hidden",
    };
    const finalLabelStyles = {
        position: "relative",
        display: "grid",
        placeItems: "center",
        width: "100%",
        height: "100%",
        outline: "thin solid black",
    };
    const finalDivStyles = {
        display: "flex",
        position: "absolute",
        right: 0,
        bottom: 0,
        zIndex: 300,
        width: "240px",
    };
    const initialState = {
        inputStyles: initialInputStyles,
        labelStyles: initialLabelStyles,
        divStyles: initialDivStyles,
    };
    const reducer = (state, action) => {
        if (action === "initial")
            return {
                inputStyles: initialInputStyles,
                labelStyles: initialLabelStyles,
                divStyles: initialDivStyles,
            };
        if (action === "hover")
            return {
                inputStyles: hoverInputStyles,
                labelStyles: hoverLabelStyles,
                divStyles: hoverDivStyles,
            };
        if (action === "final")
            return {
                inputStyles: finalInputStyles,
                labelStyles: finalLabelStyles,
                divStyles: finalDivStyles,
            };
        return Object.assign({}, state);
    };
    const [state, dispatch] = useReducer(reducer, initialState);
    useEffect(() => {
        if (image)
            dispatch("final");
        const preventDefault = (event) => {
            event.preventDefault();
        };
        const preventDefaultDrop = (event) => {
            dispatch("final");
            event.preventDefault();
        };
        const dragEnter = () => {
            dispatch("hover");
        };
        const dragExit = () => {
            dispatch(Boolean(image) ? "final" : "initial");
        };
        window.addEventListener("dragover", preventDefault);
        window.addEventListener("drop", preventDefaultDrop);
        window.addEventListener("dragenter", dragEnter);
        window.addEventListener("dragexit", dragExit);
        return () => {
            window.removeEventListener("dragover", preventDefault);
            window.removeEventListener("drop", preventDefault);
            window.removeEventListener("dragenter", dragEnter);
            window.removeEventListener("dragexit", dragExit);
        };
    }, []);
    //https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/File_drag_and_drop
    function dropHandler(ev) {
        ev.preventDefault();
        const files = ev.dataTransfer ? ev.dataTransfer.files : null;
        handleDrop && handleDrop(files, ev);
        getFiles && getFiles(files);
        dispatch("initial");
    }
    const [inputValue, setInputValue] = useState();
    return (React.createElement("div", { onDrop: dropHandler, style: state.divStyles },
        React.createElement("label", { id: "drop_zone", htmlFor: "romaine-folder-input", style: state.labelStyles },
            React.createElement("input", Object.assign({ type: "file", value: inputValue }, props, { style: Object.assign(Object.assign({}, state.inputStyles), props.style), id: "romaine-folder-input", onChange: (e) => {
                    props.onChange && props.onChange(e);
                    setInputValue(e.target.value);
                    getFiles && getFiles(e.target.files);
                    dispatch("initial");
                } })),
            children)));
};

const IconWrapper = (_a) => {
    var { selected, children, tooltip } = _a, props = __rest(_a, ["selected", "children", "tooltip"]);
    const { romaine: { mode }, } = useRomaine();
    const [hover, sethover] = useState(false);
    return (React.createElement("abbr", { title: tooltip },
        React.createElement("div", Object.assign({ onMouseEnter: () => sethover(true), onMouseLeave: () => sethover(false), style: {
                border: selected === true || selected === mode || hover
                    ? "thin solid black"
                    : "",
                borderRadius: "4px",
                backgroundColor: hover
                    ? "#888"
                    : selected === true || selected === mode
                        ? "#555"
                        : "#fff0",
                display: "grid",
                placeItems: "center",
                height: "40px",
            } }, props), children)));
};

const CropperIcon = (props) => {
    const { setMode } = useRomaine();
    useEffect(() => {
        // using keydown because it already requires another key to be pressed
        const eventListenerCropper = (e) => {
            if (!e.ctrlKey && e.shiftKey && e.key === "C") {
                e.preventDefault();
                setMode && setMode("crop");
            }
        };
        window.removeEventListener("keydown", eventListenerCropper);
        window.addEventListener("keydown", eventListenerCropper);
        return () => {
            window.removeEventListener("keydown", eventListenerCropper);
        };
    }, []);
    return (React.createElement(IconWrapper, Object.assign({}, props, { onClick: () => setMode && setMode("crop"), selected: "crop", tooltip: "Crop Tool (shift+C)" }),
        React.createElement("svg", { stroke: "currentColor", fill: "none", strokeWidth: "2", viewBox: "0 0 24 24", strokeLinecap: "round", strokeLinejoin: "round", height: "25px", width: "25px", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("path", { d: "M6.13 1L6 16a2 2 0 0 0 2 2h15" }),
            React.createElement("path", { d: "M1 6.13L16 6a2 2 0 0 1 2 2v15" }))));
};

const PerspectiveIcon = (props) => {
    const { setMode } = useRomaine();
    useEffect(() => {
        // using keydown because it already requires another key to be pressed
        const eventListenerPerspective = (e) => {
            if (e.ctrlKey && e.shiftKey && e.key === "C") {
                e.preventDefault();
                setMode && setMode("perspective-crop");
            }
        };
        window.removeEventListener("keydown", eventListenerPerspective);
        window.addEventListener("keydown", eventListenerPerspective);
        return () => {
            window.removeEventListener("keydown", eventListenerPerspective);
        };
    }, []);
    return (React.createElement(IconWrapper, Object.assign({}, props, { onClick: () => setMode && setMode("perspective-crop"), selected: "perspective-crop", tooltip: "Perspective Cropper (Ctrl + Shift + C)" }),
        React.createElement("svg", { stroke: "currentColor", fill: "currentColor", strokeWidth: "0", viewBox: "0 0 512 512", height: "25px", width: "25px", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("path", { d: "M208 151c-28 0-60.378 17.102-90.992 37.512-30.615 20.41-58.84 44.594-75.37 61.125L35.27 256l6.367 6.363c16.53 16.53 44.756 40.716 75.37 61.125C147.623 343.898 180 361 208 361h3.73l2.633-2.637c8.644-8.643 15.787-18.62 21.49-29.47-20.47-1.078-36.916-10.11-47.767-23.13C176.512 291.872 171 273.874 171 256c0-17.875 5.512-35.873 17.086-49.762 10.85-13.02 27.297-22.053 47.768-23.13-5.704-10.85-12.847-20.828-21.49-29.47L211.728 151H208zm32 50c-17.6 0-29.66 6.65-38.086 16.762C193.488 227.872 189 241.875 189 256s4.488 28.127 12.914 38.238C210.34 304.348 222.4 311 240 311h3.7c1.89-5.276 3.485-10.685 4.796-16.182-2.5 1.36-5.324 2.182-8.496 2.182-9.282 0-15.65-6.92-19.363-14.348-3.715-7.428-5.637-16.6-5.637-26.652 0-10.053 1.922-19.224 5.637-26.652C224.35 221.918 230.717 215 240 215c3.172 0 5.995.822 8.496 2.182-1.31-5.497-2.905-10.906-4.797-16.182H240zm176 7v32h-96v32h96v32l48-48-48-48zm-168 16a8 16 0 0 0-8 16 8 16 0 0 0 8 16 8 16 0 0 0 8-16 8 16 0 0 0-8-16z" }))));
};

/**
 * @todo
 * 1) Need the ability to change angle for rotation
 *    1) Need to add angle of rotation to the Romaine context and create a reducer helper to change value
 *
 *
 *    This needs to be done in romaine not romaine-components (here)
 *    2) Need to dynamically add an input that can change the angle of rotation
 * @copyright The SVG comes from: Ant Design Icons https://github.com/ant-design/ant-design-icons
 * @license MIT
 */
const RotateLeft = (props) => {
    const { setMode } = useRomaine();
    return (React.createElement(IconWrapper, Object.assign({}, props, { onClick: () => setMode && setMode("rotate-left"), selected: "rotate-left" }),
        React.createElement("svg", { stroke: "currentColor", fill: "currentColor", strokeWidth: "0", viewBox: "0 0 1024 1024", version: "1.1", height: "25px", width: "25px", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("defs", null),
            React.createElement("path", { d: "M672 418H144c-17.7 0-32 14.3-32 32v414c0 17.7 14.3 32 32 32h528c17.7 0 32-14.3 32-32V450c0-17.7-14.3-32-32-32z m-44 402H188V494h440v326z" }),
            React.createElement("path", { d: "M819.3 328.5c-78.8-100.7-196-153.6-314.6-154.2l-0.2-64c0-6.5-7.6-10.1-12.6-6.1l-128 101c-4 3.1-3.9 9.1 0 12.3L492 318.6c5.1 4 12.7 0.4 12.6-6.1v-63.9c12.9 0.1 25.9 0.9 38.8 2.5 42.1 5.2 82.1 18.2 119 38.7 38.1 21.2 71.2 49.7 98.4 84.3 27.1 34.7 46.7 73.7 58.1 115.8 11 40.7 14 82.7 8.9 124.8-0.7 5.4-1.4 10.8-2.4 16.1h74.9c14.8-103.6-11.3-213-81-302.3z" }))));
};

/**
 * @copyright Ant Design Icons https://github.com/ant-design/ant-design-icons
 * @license MIT
 */
const RotateRight = (props) => {
    const { setMode } = useRomaine();
    return (React.createElement(IconWrapper, Object.assign({ selected: "rotate-right" }, props, { onClick: () => setMode && setMode("rotate-right") }),
        React.createElement("svg", { stroke: "currentColor", fill: "currentColor", strokeWidth: "0", viewBox: "0 0 1024 1024", version: "1.1", height: "25px", width: "25px", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("defs", null),
            React.createElement("path", { d: "M480.5 251.2c13-1.6 25.9-2.4 38.8-2.5v63.9c0 6.5 7.5 10.1 12.6 6.1L660 217.6c4-3.2 4-9.2 0-12.3l-128-101c-5.1-4-12.6-0.4-12.6 6.1l-0.2 64c-118.6 0.5-235.8 53.4-314.6 154.2-69.6 89.2-95.7 198.6-81.1 302.4h74.9c-0.9-5.3-1.7-10.7-2.4-16.1-5.1-42.1-2.1-84.1 8.9-124.8 11.4-42.2 31-81.1 58.1-115.8 27.2-34.7 60.3-63.2 98.4-84.3 37-20.6 76.9-33.6 119.1-38.8z" }),
            React.createElement("path", { d: "M880 418H352c-17.7 0-32 14.3-32 32v414c0 17.7 14.3 32 32 32h528c17.7 0 32-14.3 32-32V450c0-17.7-14.3-32-32-32z m-44 402H396V494h440v326z" }))));
};

const RomaineExample = ({ setBlob, image = "https://source.unsplash.com/random", }) => {
    return (React.createElement(Romaine, null,
        React.createElement(ChildComponent, { setBlob: setBlob, image: image })));
};
/**
 *
 * @todo 1) move the get blob button into its own file
 */
const ChildComponent = ({ setBlob, image }) => {
    const RomaineRef = useRef(null);
    const { loaded, setMode } = useRomaine();
    const [state, setstate] = useState(image);
    return (React.createElement("div", { style: { marginTop: "4em" } }, loaded && (React.createElement(Canvas, { ref: RomaineRef, image: state, maxHeight: 500, maxWidth: 500, onChange: () => { }, onDragStop: () => { }, pointSize: 5, lineWidth: 1, wrapperProps: { style: { border: "thin solid black" } } },
        React.createElement(FolderSelection, { image: state, getFiles: (files) => setstate(files && files[0]) },
            React.createElement("span", { style: { display: "grid", placeItems: "center" } }, state ? "Choose a Different File" : "Choose or Drag a File Here")),
        React.createElement("button", { style: {
                position: "absolute",
                right: 0,
                bottom: "3ch",
                zIndex: 400,
                border: "thin solid black",
                borderRadius: 0,
                fontSize: "16px",
                background: "white",
                width: "240px",
            }, onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                setMode && setMode(null);
                // need to let mode actually get set to null
                // React 18 useTransition would be nice here...
                // but for backwards compatability doing this...
                setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                    var _a, _b, _c, _d;
                    if (setBlob && ((_a = RomaineRef.current) === null || _a === void 0 ? void 0 : _a.getBlob)) {
                        const newBlob = (yield ((_b = RomaineRef.current) === null || _b === void 0 ? void 0 : _b.getBlob({
                            type: "image/jpeg",
                        }))) || null;
                        setBlob(newBlob);
                    }
                    else {
                        if ((_c = RomaineRef.current) === null || _c === void 0 ? void 0 : _c.getBlob)
                            console.log("Image as Blob: ", yield ((_d = RomaineRef.current) === null || _d === void 0 ? void 0 : _d.getBlob({ type: "image/jpeg" })));
                        console.warn("You must give the example setBlob as an input");
                    }
                }), 0);
            }) }, "Export Image"),
        React.createElement("div", { style: {
                position: "absolute",
                top: "0",
                bottom: "0",
                right: "0",
                width: "240px",
            } },
            React.createElement("div", { style: { display: "grid", gridTemplateColumns: "repeat(6, 1fr)" } },
                React.createElement(RotateLeft, null),
                React.createElement(RotateRight, null),
                React.createElement(CropperIcon, null),
                React.createElement(PerspectiveIcon, null)))))));
};

export { ChildComponent, CropperIcon, FolderSelection, IconWrapper, PerspectiveIcon, RomaineExample, RotateLeft, RotateRight };
//# sourceMappingURL=romaine_components.development.esm.jsx.map
